import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../../utils/api';
import '../../styles/landing.css';

const Contact = () => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const nav = document.querySelector('.landing-nav');
        const handleScroll = () => {
            if (window.scrollY > 50) {
                nav?.classList.add('scrolled');
            } else {
                nav?.classList.remove('scrolled');
            }
        };
        window.addEventListener('scroll', handleScroll);

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in-view');
                    obs.unobserve(entry.target);
                }
            });
        }, observerOptions);

        document.querySelectorAll('.scroll-animate').forEach((element) => {
            observer.observe(element);
        });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const formData = {
                firstName: e.target.firstName.value,
                lastName: e.target.lastName.value,
                email: e.target.email.value,
                message: e.target.message.value
            };

            const response = await api.post('/contact', formData);

            const data = response.data;

            if (data.success) {
                alert('Thank you! Your request has been sent. Our team will contact you shortly.');
                e.target.reset();
            } else {
                alert(data.message || 'Failed to send message. Please try again.');
            }
        } catch (error) {
            console.error('Submission error:', error);
            alert('An error occurred. Please try again later.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="landing-body" style={{ paddingTop: '80px' }}>
            <Helmet>
                <title>Envion | Book a Demo & Contact Us</title>
                <meta name="description" content="Book a demo or get in touch with the Envion team. See how our AI-powered onboarding and preboarding platform can work for your organisation." />
                <link rel="canonical" href="https://envion.org.uk/contact" />
                <meta property="og:title" content="Envion" />
                <meta property="og:description" content="Book a demo or get in touch with the Envion team. See how our AI-powered onboarding and preboarding platform can work for your organisation." />
                <meta property="og:image" content="Will be provided" />
                <meta property="og:url" content="https://envion.org.uk/contact" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Envion" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Envion" />
                <meta name="twitter:description" content="Book a demo or get in touch with the Envion team. See how our AI-powered onboarding and preboarding platform can work for your organisation." />
                <meta name="twitter:image" content="Will be provided" />
            </Helmet>
            <style>
                {`
                    .form-group {
                        position: relative;
                    }
            
                    .form-label {
                        display: block;
                        font-size: 0.8rem;
                        font-weight: 700;
                        color: var(--lp-secondary-navy);
                        margin-bottom: 6px;
                        text-transform: uppercase;
                        letter-spacing: 0.05em;
                    }
            
                    .form-input {
                        width: 100%;
                        padding: 12px 16px;
                        border: 1px solid var(--lp-border);
                        border-radius: 12px;
                        font-family: 'Outfit', sans-serif;
                        font-size: 0.95rem;
                        background: #f8fafc;
                        transition: all 0.3s ease;
                        box-sizing: border-box;
                        color: var(--lp-secondary-navy);
                    }
            
                    .form-input:focus {
                        outline: none;
                        border-color: var(--lp-primary-orange);
                        background: white;
                        box-shadow: 0 0 0 4px rgba(232, 111, 74, 0.05);
                    }
            
                    .form-error {
                        font-size: 0.7rem;
                        color: #ef4444;
                        margin-top: 4px;
                        font-weight: 500;
                    }
            
                    .contact-info-card {
                        background: white;
                        padding: 24px;
                        border-radius: 20px;
                        border: 1px solid var(--lp-border);
                        transition: all 0.3s ease;
                        text-decoration: none;
                        display: block;
                        color: inherit;
                    }
            
                    .contact-info-card:hover {
                        transform: translateY(-3px);
                        border-color: var(--lp-primary-orange);
                        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
                    }
            
                    @media (max-width: 968px) {
                        .contact-split {
                            grid-template-columns: 1fr !important;
                            gap: 40px !important;
                        }
            
                        section {
                            height: auto !important;
                            padding: 100px 20px 40px !important;
                        }

                        .contact-split h1 {
                            font-size: 2rem !important;
                        }

                        .contact-split form div[style*="grid-template-columns: 1fr 1fr"] {
                            grid-template-columns: 1fr !important;
                        }
                    }
                `}
            </style>

            {/* Navigation */}
            <nav className={`landing-nav${menuOpen ? ' nav-open' : ''}`}>
                <div className="logo">
                    <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fa-solid fa-layer-group logo-icon" style={{ color: 'var(--lp-primary-orange)' }}></i>
                        <span style={{ color: 'var(--lp-primary-orange)' }}>envion</span>
                    </Link>
                </div>
                <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
                    <i className={menuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'}></i>
                </button>
                <div className="nav-links">
                    <div className="nav-item-dropdown">
                        <Link to="/solutions" className="nav-link">Solutions <i className="fa-solid fa-chevron-down" style={{ fontSize: '0.7em', marginLeft: '6px', opacity: 0.7 }}></i></Link>
                        <div className="dropdown-menu">
                            <Link to="/solutions#use-cases" className="dropdown-item">
                                <div className="dropdown-content">
                                    <div className="dropdown-header">
                                        <strong>Preboarding</strong>
                                        <i className="fa-solid fa-arrow-right dropdown-arrow"></i>
                                    </div>
                                    <span>Engagement starts early.</span>
                                </div>
                            </Link>
                            <Link to="/solutions#use-cases" className="dropdown-item">
                                <div className="dropdown-content">
                                    <div className="dropdown-header">
                                        <strong>Onboarding</strong>
                                        <i className="fa-solid fa-arrow-right dropdown-arrow"></i>
                                    </div>
                                    <span>Accelerate ramp-up time.</span>
                                </div>
                            </Link>
                        </div>
                    </div>
                    <Link to="/about" className="nav-link">About Us</Link>
                    <Link to="/#how-it-works" className="nav-link">How it Works</Link>
                    <Link to="/contact" className="nav-link" style={{ color: 'var(--lp-primary-orange)' }}>Contact</Link>
                </div>
                <div className="auth-buttons">
                    <button onClick={() => navigate('/login')} className="btn-lp btn-lp-secondary w-full sm:w-auto text-center" style={{ cursor: 'pointer' }}>Log In</button>
                    <Link to="/contact" className="btn-lp btn-lp-primary">Get a Quote</Link>
                </div>
            </nav>

            {/* Optimised Screen-Fit Section */}
            <section style={{ height: 'calc(100vh - 80px)', display: 'flex', alignItems: 'center', padding: '40px', background: 'white', overflow: 'hidden' }}>
                <div style={{ maxWidth: '1250px', margin: '0 auto', width: '100%' }}>
                    <div className="contact-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '80px', alignItems: 'center' }}>
                        {/* Left: Content & Interactive Info */}
                        <div className="scroll-animate animate-slide-up" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                            <div>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(232, 111, 74, 0.08)', padding: '6px 14px', borderRadius: '20px', border: '1px solid rgba(232, 111, 74, 0.2)', marginBottom: '24px' }}>
                                    <i className="fa-solid fa-envelope" style={{ fontSize: '0.7rem', color: 'var(--lp-primary-orange)' }}></i>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--lp-primary-orange)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Contact Us</span>
                                </div>
                                <h1 style={{ fontSize: '3.5rem', fontWeight: 800, color: 'var(--lp-secondary-navy)', marginBottom: '16px', lineHeight: 1.1 }}>
                                    Get in Touch</h1>
                                <p style={{ fontSize: '1.1rem', color: 'var(--lp-text-muted)', lineHeight: 1.6, marginBottom: 0 }}>
                                    Ready to transform your onboarding experience? Our team is here to help you build a custom
                                    solution tailored to your team's needs.
                                </p>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                {/* Book a Meeting Card */}
                                <a href="https://outlook.office.com/book/DiscoveryCall@envion.org.uk/?ismsaljsauthenabled"
                                    target="_blank" rel="noopener noreferrer"
                                    className="contact-info-card scroll-animate animate-slide-up delay-100"
                                    style={{ gridColumn: 'span 2' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                                        <div style={{ width: '40px', height: '40px', background: 'rgba(232, 111, 74, 0.05)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className="fa-solid fa-calendar-plus" style={{ color: 'var(--lp-primary-orange)' }}></i>
                                        </div>
                                        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--lp-secondary-navy)', margin: 0 }}>
                                            Book a Meeting</h3>
                                    </div>
                                    <p style={{ fontSize: '0.9rem', color: 'var(--lp-text-muted)', margin: 0 }}>Schedule a 30-minute
                                        discovery call with our onboarding experts.</p>
                                </a>

                                {/* Email Card */}
                                <a href="mailto:enquiries@envion.org.uk"
                                    className="contact-info-card scroll-animate animate-slide-up delay-200"
                                    style={{ gridColumn: 'span 2' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
                                        <div style={{ width: '40px', height: '40px', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className="fa-solid fa-at" style={{ color: '#10b981' }}></i>
                                        </div>
                                        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--lp-secondary-navy)', margin: 0 }}>
                                            Email Us</h3>
                                    </div>
                                    <p style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--lp-secondary-navy)', margin: 0 }}>
                                        enquiries@envion.org.uk</p>
                                </a>
                            </div>
                        </div>

                        {/* Right: Contact Form (Professional & Compact) */}
                        <div className="scroll-animate animate-slide-up delay-300"
                            style={{ background: 'white', padding: '40px', borderRadius: '32px', border: '1px solid var(--lp-border)', boxShadow: '0 40px 80px rgba(0,0,0,0.05)' }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--lp-secondary-navy)', marginBottom: '24px' }}>
                                Send us a message</h2>

                            <form onSubmit={handleSubmit}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <div className="form-group">
                                        <label className="form-label">First Name</label>
                                        <input type="text" id="firstName" className="form-input" placeholder="John" required />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Last Name</label>
                                        <input type="text" id="lastName" className="form-input" placeholder="Doe" required />
                                    </div>
                                </div>

                                <div className="form-group" style={{ marginBottom: '16px' }}>
                                    <label className="form-label">Email</label>
                                    <input type="email" id="email" className="form-input" placeholder="john@company.com" required />
                                </div>

                                <div className="form-group" style={{ marginBottom: '24px' }}>
                                    <label className="form-label">Message</label>
                                    <textarea id="message" className="form-input" rows="4"
                                        placeholder="Tell us about your requirements..." style={{ resize: 'none' }} minLength="10"
                                        required></textarea>
                                </div>

                                <button type="submit"
                                    disabled={submitting}
                                    style={{
                                        width: '100%', padding: '14px', background: 'var(--lp-primary-orange)', color: 'white',
                                        border: 'none', borderRadius: '12px', fontWeight: 800, fontSize: '1.1rem', cursor: submitting ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease', boxShadow: '0 10px 20px rgba(232, 111, 74, 0.2)', opacity: submitting ? 0.8 : 1
                                    }}
                                    onMouseOver={(e) => !submitting && (e.currentTarget.style.transform = 'translateY(-2px)', e.currentTarget.style.boxShadow = '0 15px 30px rgba(232, 111, 74, 0.3)')}
                                    onMouseOut={(e) => !submitting && (e.currentTarget.style.transform = 'translateY(0)', e.currentTarget.style.boxShadow = '0 10px 20px rgba(232, 111, 74, 0.2)')}>
                                    {submitting ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Sending...</> : 'Get a Quote'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="site-footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <h2><i className="fa-solid fa-layer-group"></i> envion</h2>
                        <p className="footer-subtitle" style={{ fontSize: '1.1rem', color: 'white', lineHeight: 1.5, marginBottom: '24px' }}>
                            envion is a modern workforce readiness platform. Enabling faster, smarter onboarding and preboarding
                            for today’s organisations.
                        </p>
                        <div className="footer-contact-info">
                            <p><i className="fa-solid fa-envelope"></i> <a href="mailto:enquiries@envion.org.uk" style={{ color: 'white', textDecoration: 'none' }}>enquiries@envion.org.uk</a></p>
                        </div>
                    </div>
                    <div className="footer-column">
                        <h3>Solutions</h3>
                        <ul className="footer-links">
                            <li><Link to="/solutions#use-cases">Onboarding</Link></li>
                            <li><Link to="/solutions#use-cases">Preboarding</Link></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Company</h3>
                        <ul className="footer-links">
                            <li><Link to="/about">About Us</Link></li>
                            <li><Link to="/contact">Contact</Link></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Connect</h3>
                        <ul className="footer-social-links" style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '16px', marginBottom: '24px' }}>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white', transition: 'opacity 0.3s' }} onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'} onMouseOut={(e) => e.currentTarget.style.opacity = '1'}><i className="fa-brands fa-instagram"></i></a></li>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white', transition: 'opacity 0.3s' }} onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'} onMouseOut={(e) => e.currentTarget.style.opacity = '1'}><i className="fa-brands fa-linkedin"></i></a></li>
                        </ul>
                        <div className="footer-compliance-badges" style={{ color: 'white', fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.4 }}>
                            <p style={{ marginBottom: '4px' }}>GDPR Compliant</p>
                            <p>Registered with ICO</p>
                        </div>
                    </div>
                </div>
                <div className="footer-bottom">
                    <div className="footer-bottom-grid">
                        <div className="footer-legal-info">
                            <p className="copyright">&copy; 2026 envion Inc. All rights reserved.</p>
                        </div>
                        <div className="footer-legal-links">
                            <Link to="/terms">Terms & Conditions</Link>
                            <Link to="/privacy">Privacy Policy</Link>
                            <Link to="/cookies">Cookies Policy</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Contact;
